import { useCallback, useEffect, useRef } from 'react';
import { pollResearchStatus } from '../../../../services/conversationHistoryService';
import { debug } from '../../../../utils/debugLogger';

interface UseResearchPollingOptions {
  threadId: string | null;
  /** Start polling only when this is true. */
  enabled: boolean;
  idToken: string;
  /** Called when polling returns status === "completed" with the research document. */
  onCompleted: (doc: Record<string, any>) => void;
  /** Called when polling returns status === "failed" or max duration is reached. */
  onFailed: (message: string) => void;
  /** Milliseconds between polls (default: 10 seconds). */
  intervalMs?: number;
  /** Stop polling after this many ms regardless of status (default: 25 minutes).
   *  Deep research itself is 3–6 min, but the SSE stream often holds the
   *  connection open with keepalives for 10–15 min before it drops and polling
   *  even starts — a 10 min budget expired mid-job and left the card spinning. */
  maxDurationMs?: number;
}

export function useResearchPolling({
  threadId,
  enabled,
  idToken,
  onCompleted,
  onFailed,
  intervalMs = 10_000,
  maxDurationMs = 25 * 60_000,
}: UseResearchPollingOptions): { stop: () => void } {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);
  const stoppedRef = useRef(false);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !threadId) return;

    stoppedRef.current = false;
    startedAtRef.current = Date.now();

    const poll = async () => {
      if (stoppedRef.current) return;

      if (Date.now() - startedAtRef.current > maxDurationMs) {
        stop();
        onFailed(
          `Research timed out after ${Math.round(maxDurationMs / 60_000)} minutes.`,
        );
        return;
      }

      try {
        const result = await pollResearchStatus(threadId, idToken);
        debug.log('[useResearchPolling] status:', result.status);

        if (result.status === 'completed' && result.research_document) {
          stop();
          onCompleted(result.research_document);
        } else if (result.status === 'failed') {
          stop();
          onFailed('The deep research job failed.');
        }
        // 'in_progress' or 'none' → keep polling
      } catch (err) {
        debug.error('[useResearchPolling] poll error (will retry):', err);
        // Network errors are transient — keep polling
      }
    };

    // Poll once immediately, then on interval
    void poll();
    timerRef.current = setInterval(poll, intervalMs);

    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, threadId, idToken]);

  return { stop };
}
